import type { ComponentType } from "react";
import { PureComponent, type JSX } from "react";
import { connect } from "react-redux";
import AdsTrafficChart from "./AdsTrafficChart";
import styled from "styled-components";
import type {
    IAdsTrafficChartSiteData,
    IAdsTrafficCompareData,
    IAdsTrafficProps,
    IAdsTrafficSingleData,
} from "./AdsTrafficTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import _ from "lodash";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { transformLegendsData } from "app/pages/website-analysis/analyze-publishers/common/ads-traffic/AdsTrafficChartParsers";

// Set the components wrapper to be a with a relative positioning
// and use absolute position to move the export PNG button to the
// chart's title. the reason we're using this method is that the
const StyledAdsTrafficContainer = styled(FlexRow)`
    position: relative;
    padding: 24px 24px 24px 13px;
`;

class AdsTraffic extends PureComponent<IAdsTrafficProps> {
    /**
     * Holds a reference to the underlying chart data. used by the PNG exporter service
     * for exporting the chart's dates ranges.
     */
    private chartRawData: IAdsTrafficChartSiteData[];

    private adaptChartData = (): IAdsTrafficChartSiteData[] => {
        const { data } = this.props;
        const { key } = this.props.params;
        const isCompareMode = key.split(",").length > 1;

        const chartData = isCompareMode
            ? this.adaptToCompareMode(data as IAdsTrafficCompareData)
            : this.adaptToSingleMode(data as IAdsTrafficSingleData);

        return chartData;
    };

    public render() {
        const adaptedChartData = this.adaptChartData();
        this.chartRawData = this.filterValidChartData(adaptedChartData);
        return this.renderChartComponent();
    }

    private renderChartComponent = (): JSX.Element => {
        const { key } = this.props.params;
        const isCompareMode = key.split(",").length > 1;

        return (
            <StyledAdsTrafficContainer>
                <AdsTrafficChart
                    isCompareMode={isCompareMode}
                    chartRawData={this.chartRawData}
                    legendItems={this.getLegendItems()}
                    afterRender={this.props.afterRender}
                />
            </StyledAdsTrafficContainer>
        );
    };

    private formattedValue = (val) => {
        const numberFormatter = (value) => minAbbrNumberFilter()(value);
        return val && val > 0 ? numberFormatter(val) : "N/A";
    };

    private getLegendItems = () => {
        const { data, chosenItems } = this.props;
        const transformedData = transformLegendsData(data);
        const checkboxFilters = chosenItems.map((checkbox: any) => {
            return {
                name: checkbox.displayName,
                hidden: false,
                visible: true,
                color: checkbox.color,
            };
        });
        if (transformedData) {
            const getWinner = () => {
                if (!transformedData) {
                    return null;
                }
                return _.maxBy(Object.keys(transformedData), (o) => {
                    return transformedData[o];
                });
            };
            const winner = getWinner();
            const total: any = Object.values(transformedData).reduce((acc: number, val: number) => acc + val, 0);
            return total
                ? checkboxFilters.map((item) => {
                      const currentLegendValue = this.formattedValue(transformedData[item.name]);
                      return {
                          data: currentLegendValue,
                          name: item.name,
                          hidden: item.hidden,
                          visible: currentLegendValue !== "N/A" ? item.visible : false,
                          isDisabled: currentLegendValue === "N/A",
                          color: item.color,
                          isWinner: item.name === winner,
                      };
                  })
                : [];
        } else {
            return [];
        }
    };

    /**
     * Process the traffic data for a single website (single chosen item)
     * used when viewing traffic data for a single website
     */
    private adaptToSingleMode = (data: IAdsTrafficSingleData): IAdsTrafficChartSiteData[] => {
        // Since we're processing the data in single mode
        // we know at this point that the chosen items prop
        // has a single item in it.
        const {
            chosenItems: [site],
        } = this.props;

        const siteData = {
            data: data?.data ?? [],
            dates: data?.dates ?? [],
            siteName: site?.displayName,
            siteColor: site?.color,
        };

        return [siteData];
    };

    /**
     * Process the traffic data for multiple websites (multiple chosen items)
     * used when viewing traffic data in compare mode.
     */
    private adaptToCompareMode = (data: IAdsTrafficCompareData): IAdsTrafficChartSiteData[] => {
        const { chosenItems } = this.props;

        const sitesData = data.data.map((site) => {
            const siteData = chosenItems?.find((x) => x.displayName === site.displayName);

            return {
                data: site?.data ?? [],
                dates: site?.dates ?? [],
                siteName: siteData?.displayName,
                siteColor: siteData?.color,
            };
        });

        return sitesData;
    };

    /**
     * Filters out any sites that have missing or invalid data. we don't want to display sites that
     * have no data whatsoever.
     */
    private filterValidChartData = (chartData: IAdsTrafficChartSiteData[]): IAdsTrafficChartSiteData[] => {
        const validSites = chartData.filter((site) => {
            const hasValidData = site.data.length > 0 && site.dates.length > 0;
            const hasValidDisplayData = !!site.siteColor && !!site.siteName;
            return hasValidData && hasValidDisplayData;
        });

        return validSites;
    };
}

const mapStateToProps = ({ routing: { chosenItems } }) => {
    return { chosenItems };
};

export default connect(mapStateToProps)(AdsTraffic as ComponentType<any>);
